import React, { useMemo, useState } from 'react';
import {
  Btn, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput, panelPost, useAdminGet,
} from './kit';
import TrlProvenance from './universities/TrlProvenance';
import { CarouselToggle, FillLabelsButton, onCarousel } from './techpages/LabelsCarousel';
import ListingRequests from './techpages/ListingRequests';
import EditableTemplates from './email/EditableTemplates';

/**
 * Universities group — the founders' "Global Universities" sheet, read straight
 * from the corpus instead of maintained by hand.
 *
 * There is no universities table: a university is a utech_university_id on the
 * technologies rows, so the backend groups those rows once and hands back one
 * row per code with the sheet's five bands (Context, Card, Homepage, Mapping,
 * Contact, Data quality) already computed. This panel's whole job is to lay
 * that out the way the sheet does — a grey band header over the column
 * header — and to make the three things the sheet was edited for editable in
 * place: the website label, the curated TTO office, and the admin-only extras
 * (QS rank, estimated tech count, notes) that have no column in the DB.
 *
 * Website label edits go through the Database group's existing action so both
 * screens keep reading one stored value.
 *
 * Sub-tabs: the sheet above (first), "TRL provenance"
 * (universities/TrlProvenance.tsx) — where each card's TRL comes from, overall
 * and per university — "Listing requests" (the /universities "Get your
 * university listed" form, techpages/ListingRequests.tsx) and "Emails" (the
 * shared template editor narrowed to the Universities group).
 *
 * Next to each label sits the homepage-carousel switch (universities/
 * set_carousel): a label no longer puts a university on the ticker by itself.
 * The same labels + switch are editable from Tech URL pages → Labels & carousel.
 */

interface Band { key: string; title: string; columns: string[] }

interface Row {
  code: string; name: string;
  website_label: string; website_label_source: 'admin' | 'seed' | 'missing';
  country: string; techs: number; researchers: number;
  managers: number; managers_with_email: number;
  manager_kind: string; manager_emails: number;
  last_updated: string | null;
  source_url: string | null; sample_tech_url: string | null; sample_ref: string | null;
  tto_office_name: string | null; tto_office_emails: string[];
  tto_office_source: 'curated' | 'scraped' | 'none'; tto_office_updated_at: string | null;
  unique_ref_pct: number; trl_explicit_pct: number; researchers_pct: number;
  ip_pct: number; publications_pct: number; price: null;
  qs_rank: string | null; estimated_tech: string | null; notes: string | null;
  /** Stored homepage-carousel switch; on the carousel = has a label AND this is true. */
  carousel?: boolean | null;
  /** The university's public page (tech_pages canonical path), once URLs are built. */
  page_path?: string | null;
}

interface Sheet {
  generated_at: string;
  bands: Band[];
  labels: Record<string, string>;
  rows: Row[];
  totals: {
    universities: number; technologies: number; researchers: number;
    curated_offices: number; without_label: number; countries: number;
  };
}

// Columns the panel can sort by from the toolbar. Anything not listed here is
// still sortable by clicking its header; the dropdown just names the ones
// people actually reach for.
const SORT_OPTIONS: Record<string, string> = {
  '-techs': 'Techs (most first)',
  'techs': 'Techs (fewest first)',
  'name': 'Name A→Z',
  'country': 'Country A→Z',
  '-researchers': 'Researchers (most first)',
  '-last_updated': 'Last updated (newest first)',
  'last_updated': 'Last updated (oldest first)',
  'unique_ref_pct': 'Unique ref % (lowest first)',
  'trl_explicit_pct': 'TRL explicit % (lowest first)',
  'researchers_pct': 'Researcher names % (lowest first)',
  'ip_pct': 'IP info % (lowest first)',
  'publications_pct': 'Publications % (lowest first)',
};

const PCT_COLS = new Set(['unique_ref_pct', 'trl_explicit_pct', 'researchers_pct',
  'ip_pct', 'publications_pct']);
const NUM_COLS = new Set(['techs', 'researchers', 'managers_with_email', 'manager_emails',
  ...PCT_COLS]);

const fmtInt = (v: any): string =>
  typeof v === 'number' ? v.toLocaleString() : (v === null || v === undefined || v === '' ? '—' : String(v));

/** The sheet shows a bare date; the timestamp's time-of-day is noise here. */
const fmtDay = (v: string | null | undefined): string => {
  if (!v) return '—';
  // A bare YYYY-MM-DD (last_updated) is a calendar day, not an instant: the
  // Date constructor reads it as UTC midnight, and a viewer west of UTC would
  // see the day before — while the CSV, which slices the string, says the
  // right day. Build it in local time so the day stays put. Real timestamps
  // (generated_at, tto_office_updated_at) carry a zone and parse as before.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

/** What a cell reads as, for filtering, sorting and the CSV — one place so
 *  the download matches what is on screen. */
const cellText = (r: Row, col: string): string => {
  switch (col) {
    case 'name': return `${r.name || ''}\n${r.code}`;
    case 'last_updated': return r.last_updated ? String(r.last_updated).slice(0, 10) : '';
    case 'manager_emails': return String(r.manager_emails ?? '');
    case 'managers_with_email': return String(r.managers_with_email ?? '');
    case 'tto_office_name':
      return r.tto_office_source === 'none' ? ''
        : `${r.tto_office_name || ''}${r.tto_office_source === 'scraped' ? ' (scraped — not curated)' : ''}`;
    case 'tto_office_emails': return (r.tto_office_emails || []).join(', ');
    case 'price': return '—';   // not tracked; the sheet's column is kept so nobody asks where it went
    case 'carousel': return onCarousel(r) ? 'Yes' : 'No';
    case 'page_path': return r.page_path || '';
    default: {
      const v = (r as any)[col];
      if (v === null || v === undefined) return '';
      if (PCT_COLS.has(col)) return v === 100 ? 'Yes' : v === 0 ? 'No' : `${v}%`;
      return String(v);
    }
  }
};

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------
const Dash: React.FC = () => <span className="text-grayscale-1100">—</span>;

/** URLs are long and the sheet only needs them clickable; show the host+path
 *  trimmed and keep the whole thing in the tooltip. */
const Link: React.FC<{ href: string | null }> = ({ href }) => {
  if (!href) return <Dash />;
  const short = href.replace(/^https?:\/\/(www\.)?/, '');
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={href}
      className="text-primary-700 underline underline-offset-2 block max-w-[16rem] truncate">
      {short}
    </a>
  );
};

/** Compact "Yes / No / 62%" with a hairline bar — the sheet's Y/N columns,
 *  but honest about the in-between. Reuses the primary/grayscale tokens only. */
const Pct: React.FC<{ value: number | null | undefined }> = ({ value }) => {
  if (value === null || value === undefined || isNaN(Number(value))) return <Dash />;
  const v = Math.max(0, Math.min(100, Math.round(Number(value))));
  const text = v === 100 ? 'Yes' : v === 0 ? 'No' : `${v}%`;
  const tone = v === 100 ? 'text-grayscale-1300 font-medium'
    : v === 0 ? 'text-grayscale-1100' : 'text-grayscale-1300';
  return (
    <span className="inline-flex flex-col gap-0.5 min-w-[3rem]" title={`${v}% of this university's technologies`}>
      <span className={`text-xs ${tone}`}>{text}</span>
      <span className="block h-1 w-12 rounded-full bg-grayscale-200 overflow-hidden">
        <span className="block h-1 rounded-full bg-primary-500" style={{ width: `${v}%` }} />
      </span>
    </span>
  );
};

/** Click-to-edit single line, the same shape DatabasePanel uses for the
 *  website label so the two screens feel like one. */
const InlineEdit: React.FC<{
  value: string | null | undefined; placeholder?: string; title?: string;
  muted?: boolean; warn?: boolean; mono?: boolean; width?: string;
  /** Resolves to an error message, or nothing when the save went through. */
  onSave: (v: string) => Promise<string | void>;
}> = ({ value, placeholder = 'set…', title, muted, warn, mono, width = 'w-32', onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const commit = async () => {
    if (busy) return;   // a second Enter while the first save is in flight must not post twice
    setBusy(true); setErr('');
    let e: string | void;
    // panelPost reports failures as a string rather than throwing, but if
    // anything upstream does throw the editor must not be left disabled.
    try { e = await onSave(draft); } catch (x: any) { e = String(x?.message || x || 'Save failed'); }
    setBusy(false);
    // A failed save keeps the editor open with the draft in it: the error is
    // shown right here, next to the input, because the panel's flash sits
    // above the KPI strip and is scrolled out of view for the lower rows —
    // and a 2000-character note is not something to retype.
    if (e) { setErr(e); return; }
    setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
        <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape' && !busy) setEditing(false);
          }}
          className={`h-7 px-1.5 ${width} rounded border-[0.5px] border-primary-300 text-xs bg-white`} />
        <Btn small disabled={busy} onClick={commit}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn small disabled={busy} onClick={() => setEditing(false)}>Cancel</Btn>
        {err && <span className="text-[11px] text-red-600 basis-full">{err}</span>}
      </span>
    );
  }
  const has = !!(value && String(value).trim());
  return (
    <button type="button"
      onClick={() => { setEditing(true); setDraft(value || ''); setErr(''); }}
      title={title || (has ? 'Click to edit' : 'Not set — click to add')}
      className={`underline underline-offset-2 decoration-dotted text-left ${mono ? 'font-mono' : ''} ${
        has ? (muted ? 'text-grayscale-1100 italic' : 'text-grayscale-1300')
            : (warn ? 'text-amber-700' : 'text-grayscale-1100')}`}>
      {has ? value : placeholder}
    </button>
  );
};

/** The Contact University pair (office name + emails) as one editable block.
 *  A scraped name is shown but tagged, because the sheet's whole point was to
 *  replace the scraper's guess with a curated office inbox. */
const OfficeCell: React.FC<{
  row: Row; onSave: (name: string, emails: string) => Promise<string>;
}> = ({ row, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [emails, setEmails] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const start = () => {
    // Pre-fill from whatever the office currently holds — scraped name too —
    // so curating is usually "confirm and add the inbox", not retyping.
    setName(row.tto_office_source === 'none' ? '' : (row.tto_office_name || ''));
    setEmails((row.tto_office_emails || []).join('\n'));
    setErr('');
    setEditing(true);
  };
  const save = async () => {
    setBusy(true); setErr('');
    const e = await onSave(name, emails);
    setBusy(false);
    if (e) setErr(e); else setEditing(false);
  };
  const copyAll = async () => {
    const text = (row.tto_office_emails || []).join(', ');
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard blocked (http, permissions) — the addresses are still visible to select */ }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5 min-w-[18rem]" onClick={e => e.stopPropagation()}>
        <input autoFocus value={name} placeholder="Office name" onChange={e => setName(e.target.value)}
          className="h-7 px-1.5 rounded border-[0.5px] border-primary-300 text-xs bg-white" />
        <textarea value={emails} rows={3} placeholder="Emails — one per line or comma-separated"
          onChange={e => setEmails(e.target.value)}
          className="px-1.5 py-1 rounded border-[0.5px] border-primary-300 text-xs bg-white font-mono resize-y" />
        {err && <div className="text-[11px] text-red-600">{err}</div>}
        <div className="flex items-center gap-1 flex-wrap">
          <Btn small kind="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Btn>
          <Btn small disabled={busy} onClick={() => setEditing(false)}>Cancel</Btn>
          <span className="text-[10px] text-grayscale-1100">empty name + emails = uncurate</span>
        </div>
      </div>
    );
  }

  const list = row.tto_office_emails || [];
  const scraped = row.tto_office_source === 'scraped';
  return (
    <div className="flex flex-col gap-1 min-w-[14rem]">
      <div className="flex items-center gap-1.5 flex-wrap">
        {row.tto_office_source === 'none' || !row.tto_office_name
          ? <span className="text-amber-700">—</span>
          : <span className={`text-sm ${scraped ? 'text-grayscale-1100 italic' : 'text-grayscale-1300 font-medium'}`}>
              {row.tto_office_name}
            </span>}
        {scraped && (
          <span className="text-[10px] uppercase tracking-wide text-grayscale-1100 border border-grayscale-300 rounded px-1"
            title="From tech_manager_default on the scraped rows — not yet curated">
            scraped — not curated
          </span>
        )}
      </div>
      {list.length ? (
        <div className="flex flex-wrap gap-1">
          {list.map(e => (
            <span key={e} className="text-[11px] font-mono rounded-full bg-grayscale-200 text-grayscale-1300 px-1.5 py-0.5"
              title={row.tto_office_source === 'curated' ? 'curated' : 'legacy common_email — not curated'}>
              {e}
            </span>
          ))}
        </div>
      ) : <span className="text-[11px] text-grayscale-1100">no email</span>}
      <div className="flex items-center gap-1">
        {!!list.length && (
          <button type="button" onClick={copyAll}
            className="text-[11px] text-primary-700 underline underline-offset-2">
            {copied ? 'Copied' : 'Copy all'}
          </button>
        )}
        <button type="button" onClick={start}
          className="text-[11px] text-primary-700 underline underline-offset-2">Edit</button>
        {row.tto_office_source === 'curated' && row.tto_office_updated_at && (
          <span className="text-[10px] text-grayscale-1100" title={row.tto_office_updated_at}>
            · curated {fmtDay(row.tto_office_updated_at)}
          </span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The sheet (first sub-tab)
// ---------------------------------------------------------------------------
const GlobalUniversities: React.FC = () => {
  const s = useAdminGet<Sheet>('universities', 'sheet');
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [onlyNotCurated, setOnlyNotCurated] = useState(false);
  const [onlyNoLabel, setOnlyNoLabel] = useState(false);
  const [onlyCarousel, setOnlyCarousel] = useState(false);
  const [sort, setSort] = useState('-techs');
  const [flash, setFlash] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const data = s.data;
  const rows = data?.rows || [];
  // Notes are admin-entered like QS rank, but the sheet has no column for them,
  // so they hang off the end in a band of their own rather than being wedged
  // into the sheet's layout.
  const bands = useMemo<Band[]>(() => data?.bands
    ? [...data.bands, { key: 'admin', title: 'Admin', columns: ['notes'] }] : [], [data]);
  const labels = useMemo<Record<string, string>>(() => ({ notes: 'Notes', ...(data?.labels || {}) }), [data]);
  const totals = data?.totals;
  const cols = useMemo(() => bands.flatMap(b => b.columns), [bands]);

  const countries = useMemo(() =>
    Array.from(new Set(rows.map(r => r.country || '').filter(Boolean))).sort(), [rows]);

  const sortKey = sort.replace(/^-/, '');
  const sortDir: 'asc' | 'desc' = sort.startsWith('-') ? 'desc' : 'asc';
  const clickSort = (c: string) => {
    // asc -> desc -> back to the server's order (techs desc), like DataTable.
    if (sortKey !== c) setSort(NUM_COLS.has(c) ? `-${c}` : c);
    else if (sortDir === (NUM_COLS.has(c) ? 'desc' : 'asc')) setSort(NUM_COLS.has(c) ? c : `-${c}`);
    else setSort('-techs');
  };

  const view = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter(r => [r.name, r.code, r.website_label, r.country, r.tto_office_name,
        ...(r.tto_office_emails || [])].some(v => String(v || '').toLowerCase().includes(needle)));
    }
    if (country) out = out.filter(r => (r.country || '') === country);
    if (onlyNotCurated) out = out.filter(r => r.tto_office_source !== 'curated');
    if (onlyNoLabel) out = out.filter(r => !r.website_label);
    if (onlyCarousel) out = out.filter(onCarousel);
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const rank = (r: Row): [number, number | string] => {
        const v = (r as any)[sortKey];
        if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return [1, ''];
        if (Array.isArray(v)) return [0, v.join(', ').toLowerCase()];
        if (typeof v === 'number') return [0, v];
        if (sortKey === 'last_updated') {
          const ms = Date.parse(String(v));
          if (!isNaN(ms)) return [0, ms];
        }
        const t = String(v).trim();
        if (t !== '' && !isNaN(Number(t))) return [0, Number(t)];
        return [0, t.toLowerCase()];
      };
      out = [...out].sort((a, b) => {
        const [ea, va] = rank(a); const [eb, vb] = rank(b);
        if (ea !== eb) return ea - eb;                       // empties last either way
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
      });
    }
    return out;
  }, [rows, q, country, onlyNotCurated, onlyNoLabel, onlyCarousel, sortKey, sortDir]);

  // Saves --------------------------------------------------------------
  const done = (r: { error: string }, okText: string) => {
    if (r.error) { setFlash({ kind: 'error', text: r.error }); return r.error; }
    setFlash({ kind: 'ok', text: okText });
    s.reload();
    return '';
  };
  // Each returns done()'s error string so the editing cell can show it in
  // place and keep the draft; '' means saved (and the sheet is reloading).
  const saveLabel = async (code: string, label: string) =>
    done(await panelPost('db', 'set_university_label', { code, label }), `Website label saved for ${code}`);
  const saveExtra = async (code: string, field: 'qs_rank' | 'estimated_tech' | 'notes', value: string) =>
    done(await panelPost('universities', 'set_extras', { code, [field]: value }), `Saved for ${code}`);
  const saveOffice = async (code: string, name: string, emails: string) =>
    done(await panelPost('universities', 'set_tto_office', { code, name, emails }), `TTO office saved for ${code}`);

  // CSV: the visible rows, in the sheet's column order, with the sheet's headers.
  const downloadCsv = () => {
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csvCols = ['code', ...cols, ...['carousel', 'page_path'].filter(c => !cols.includes(c))];
    const head = csvCols.map(c => esc(c === 'code' ? 'UTECH code' : c === 'carousel' ? (labels[c] || 'Homepage carousel')
      : c === 'page_path' ? (labels[c] || 'University page') : (labels[c] || c)));
    const body = view.map(r => csvCols.map(c => esc(
      c === 'code' ? r.code : c === 'name' ? (r.name || '') : cellText(r, c))).join(','));
    const csv = [head.join(',')].concat(body).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `global_universities_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Cells --------------------------------------------------------------
  const cell = (r: Row, c: string): React.ReactNode => {
    switch (c) {
      case 'qs_rank':
        return <InlineEdit value={r.qs_rank} width="w-16" onSave={v => saveExtra(r.code, 'qs_rank', v)} />;
      case 'estimated_tech':
        return <InlineEdit value={r.estimated_tech} width="w-20" onSave={v => saveExtra(r.code, 'estimated_tech', v)} />;
      case 'notes':
        return <InlineEdit value={r.notes} width="w-48" onSave={v => saveExtra(r.code, 'notes', v)} />;
      case 'name':
        // Fixed width, wrapping: this column is pinned to the left edge, so if
        // the longest name set its width unwrapped (~400px for CSIR / SRMIST)
        // it would cover a phone's whole viewport and no other column could
        // ever scroll into view.
        return (
          <div className="w-44 md:w-56 whitespace-normal break-words">
            <div className="font-bold text-grayscale-1300 leading-tight">{r.name || <Dash />}</div>
            <div className="text-[11px] font-mono text-grayscale-1100 flex items-center gap-2 flex-wrap">
              {r.code}
              {r.page_path && !cols.includes('page_path') && (
                <a href={r.page_path} target="_blank" rel="noopener noreferrer" title={`Public page: ${r.page_path}`}
                  className="font-sans text-primary-700 underline underline-offset-2">Page ↗</a>
              )}
            </div>
          </div>
        );
      case 'website_label':
        return (
          <span className="inline-flex items-center gap-2">
            <InlineEdit value={r.website_label} warn muted={r.website_label_source === 'seed'}
              title={r.website_label_source === 'seed'
                ? 'Carried over from the landing page — click to make it official. It is also the university part of the page URLs.'
                : r.website_label ? 'Short name on the homepage carousel and in the page URLs — click to edit'
                : 'Not set — add a short name to use it on the carousel and in the page URLs'}
              onSave={v => saveLabel(r.code, v)} />
            {!cols.includes('carousel') && (
              <CarouselToggle row={r} onSaved={t => done({ error: '' }, t)} onError={t => setFlash({ kind: 'error', text: t })} />
            )}
          </span>
        );
      case 'carousel':
        return <CarouselToggle row={r} onSaved={t => done({ error: '' }, t)} onError={t => setFlash({ kind: 'error', text: t })} />;
      case 'page_path':
        return r.page_path ? (
          <a href={r.page_path} target="_blank" rel="noopener noreferrer" title={r.page_path}
            className="text-primary-700 underline underline-offset-2 font-mono text-xs">{r.page_path} ↗</a>
        ) : <Dash />;
      case 'country': return r.country || <Dash />;
      case 'techs': case 'researchers': return <span className="tabular-nums">{fmtInt((r as any)[c])}</span>;
      case 'managers_with_email':
        return (
          <span className="tabular-nums" title={`${fmtInt(r.managers)} named managers, ${fmtInt(r.managers_with_email)} with an email`}>
            {fmtInt(r.managers_with_email)}
            {r.managers > r.managers_with_email && (
              <span className="text-grayscale-1100 text-xs"> / {fmtInt(r.managers)}</span>
            )}
          </span>
        );
      case 'last_updated': return <span title={r.last_updated || undefined}>{fmtDay(r.last_updated)}</span>;
      case 'source_url': return <Link href={r.source_url} />;
      case 'sample_tech_url': return <Link href={r.sample_tech_url} />;
      case 'sample_ref': return r.sample_ref ? <span className="font-mono text-xs">{r.sample_ref}</span> : <Dash />;
      case 'manager_kind': return r.manager_kind && r.manager_kind !== '—' ? r.manager_kind : <Dash />;
      case 'manager_emails': return <span className="tabular-nums">{r.manager_emails ? fmtInt(r.manager_emails) : <Dash />}</span>;
      // One block spans both Contact columns; the second one renders nothing.
      case 'tto_office_name': return <OfficeCell row={r} onSave={(n, e) => saveOffice(r.code, n, e)} />;
      case 'tto_office_emails': return null;   // folded into the office block above
      case 'price': return <Dash />;
      default:
        if (PCT_COLS.has(c)) return <Pct value={(r as any)[c]} />;
        return cellText(r, c) || <Dash />;
    }
  };

  const th = 'text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap select-none';
  const filtered = q.trim() || country || onlyNotCurated || onlyNoLabel || onlyCarousel;
  const carouselCount = rows.filter(onCarousel).length;

  return (
    <div className="flex flex-col gap-4">
      {s.error && <Msg kind="error">{s.error}</Msg>}
      {flash && (
        <div className="flex items-center gap-2">
          <div className="flex-1"><Msg kind={flash.kind}>{flash.text}</Msg></div>
          <button type="button" onClick={() => setFlash(null)}
            className="text-xs text-grayscale-1100 underline">dismiss</button>
        </div>
      )}
      {s.loading && !data ? <Loading label="Reading the corpus…" /> : data && (
        <>
          <KPIRow>
            <KPI label="Universities" value={fmtInt(totals?.universities)} />
            <KPI label="Technologies" value={fmtInt(totals?.technologies)} />
            <KPI label="Researchers" value={fmtInt(totals?.researchers)} />
            <KPI label="Curated TTO offices" value={fmtInt(totals?.curated_offices)}
              hint={totals ? `${fmtInt(totals.universities - totals.curated_offices)} still scraped or empty` : undefined}
              onClick={() => setOnlyNotCurated(v => !v)} active={onlyNotCurated} />
            <KPI label="Without website label" value={fmtInt(totals?.without_label)}
              onClick={() => setOnlyNoLabel(v => !v)} active={onlyNoLabel} />
            <KPI label="On homepage carousel" value={fmtInt(carouselCount)}
              hint="labelled and switched on"
              onClick={() => setOnlyCarousel(v => !v)} active={onlyCarousel} />
            <KPI label="Countries" value={fmtInt(totals?.countries)} />
          </KPIRow>

          <Section title="Global Universities"
            right={
              <span className="text-[11px] text-grayscale-1100 inline-flex items-center gap-2 flex-wrap">
                generated {fmtDay(data.generated_at)}
                <FillLabelsButton rows={rows}
                  onDone={(text, kind) => (kind === 'ok' ? done({ error: '' }, text) : setFlash({ kind: 'error', text }))} />
                <Btn small onClick={s.reload}>{s.loading ? 'Refreshing…' : 'Refresh'}</Btn>
              </span>
            }>
            <p className="text-xs text-grayscale-1100 -mt-1 mb-2">
              <b>UTECH Website Label</b> = the short name (&quot;MIT&quot;, &quot;IIT Bombay&quot;) used on the
              homepage university carousel, anywhere the full name is too long, and as the university part of
              every public page URL (renaming it moves the pages; old URLs redirect). The <b>switch</b> next to
              it decides whether the university scrolls on the homepage carousel — it needs a label. Italic
              labels are seeds carried over from the old landing page. Saving a label or flipping the switch
              rebuilds the homepage doc and the page URLs in the background (about 20 seconds).
            </p>
            <p className="text-xs text-grayscale-1100 -mt-1">
              One row per <span className="font-mono">utech_university_id</span>, computed from the
              technologies, researchers and tech_managers tables. Dotted-underlined cells are
              editable in place. Percentages are the share of that university&apos;s technologies
              that have the thing; Price is not tracked yet.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <TextInput value={q} onChange={setQ} placeholder="Filter by name, code, label, country, office…" wide />
              <Select value={country} onChange={setCountry} options={['', ...countries]}
                labels={{ '': 'All countries' }} />
              <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 whitespace-nowrap cursor-pointer">
                <input type="checkbox" checked={onlyNotCurated} onChange={e => setOnlyNotCurated(e.target.checked)} />
                Only not curated
              </label>
              <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 whitespace-nowrap cursor-pointer">
                <input type="checkbox" checked={onlyNoLabel} onChange={e => setOnlyNoLabel(e.target.checked)} />
                Only without label
              </label>
              <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 whitespace-nowrap cursor-pointer">
                <input type="checkbox" checked={onlyCarousel} onChange={e => setOnlyCarousel(e.target.checked)} />
                Only on carousel
              </label>
              <Select value={SORT_OPTIONS[sort] ? sort : ''} onChange={v => v && setSort(v)}
                options={[...(SORT_OPTIONS[sort] ? [] : ['']), ...Object.keys(SORT_OPTIONS)]}
                labels={{ ...SORT_OPTIONS, '': `Sorted by ${labels[sortKey] || sortKey} ${sortDir === 'asc' ? '↑' : '↓'}` }} />
              <span className="text-xs text-grayscale-1100">
                {view.length === rows.length ? `${rows.length} universities` : `${view.length} of ${rows.length}`}
              </span>
              {(filtered || sort !== '-techs') && (
                <button type="button"
                  onClick={() => { setQ(''); setCountry(''); setOnlyNotCurated(false); setOnlyNoLabel(false); setOnlyCarousel(false); setSort('-techs'); }}
                  className="text-xs text-grayscale-1100 underline underline-offset-2">reset</button>
              )}
              <Btn small onClick={downloadCsv} disabled={!view.length}>Download CSV</Btn>
            </div>

            {!rows.length ? (
              <div className="text-sm text-grayscale-1100 py-4 text-center">No university codes found in the corpus.</div>
            ) : !view.length ? (
              <div className="text-sm text-grayscale-1100 py-4 text-center">No university matches these filters.</div>
            ) : (
              // The section scrolls sideways on its own; the page never does.
              // The whole thead is sticky (the DataTable pattern) so the band
              // row and the column row move as one and neither has to know
              // the other's height; the name column is sticky-left on top of
              // that, so band and university stay in view however far you drag.
              <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '70vh' }}>
                <table className="text-sm border-separate border-spacing-0 min-w-max">
                  <thead className="sticky top-0 z-20">
                    <tr>
                      {bands.map((b, i) => (
                        // The band holding the name column pins with it, so the
                        // sheet's "Cosmos Tech Card" grey cell rides along on scroll.
                        <th key={b.key} colSpan={b.columns.length}
                          className={`bg-primary-100 text-primary-700
                            text-[10px] uppercase tracking-wider font-grotesk font-bold px-3 py-1.5 text-left align-bottom
                            ${i ? 'border-l border-grayscale-400' : ''}
                            ${b.columns.includes('name') ? 'sticky left-0 z-30' : ''}`}>
                          {/* Capped and allowed to wrap: the long "Cosmos Tech Card"
                              title must not be what sets the pinned column's width. */}
                          <div className="max-w-[11rem] md:max-w-[14rem] whitespace-normal leading-4">{b.title}</div>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {bands.map((b, bi) => b.columns.map((c, ci) => {
                        const sorted = sortKey === c;
                        const first = bi === 0 && ci === 0;
                        const isName = c === 'name';
                        return (
                          <th key={c} onClick={() => clickSort(c)}
                            title={sorted ? (sortDir === 'asc' ? 'Sorted ascending — click to flip' : 'Sorted descending — click to flip')
                                          : 'Click to sort'}
                            className={`${th} bg-grayscale-200 cursor-pointer hover:text-grayscale-1300
                              ${sorted ? 'text-grayscale-1300 font-bold' : 'text-grayscale-1200'}
                              ${ci === 0 && !first ? 'border-l border-grayscale-400' : ''}
                              ${isName ? 'sticky left-0 z-30 border-r' : ''}`}>
                            {labels[c] || c}
                            <span aria-hidden className="ml-1 text-[10px] opacity-70">
                              {sorted ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                            </span>
                          </th>
                        );
                      }))}
                    </tr>
                  </thead>
                  <tbody>
                    {view.map(r => (
                      <tr key={r.code} className="group hover:bg-grayscale-200">
                        {bands.map((b, bi) => b.columns.map((c, ci) => {
                          const isName = c === 'name';
                          // The office name + emails are one editable block under
                          // two sheet headers: the name cell spans both, the
                          // emails cell is not rendered.
                          if (c === 'tto_office_emails') return null;
                          return (
                            <td key={c} colSpan={c === 'tto_office_name' ? 2 : undefined}
                              className={`px-3 py-2 align-top border-t border-grayscale-200 text-grayscale-1300
                                ${ci === 0 && bi ? 'border-l border-grayscale-300' : ''}
                                ${isName ? 'sticky left-0 z-[5] bg-white group-hover:bg-grayscale-200 border-r' : ''}
                                ${PCT_COLS.has(c) ? 'whitespace-nowrap' : ''}`}>
                              {cell(r, c)}
                            </td>
                          );
                        }))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The panel: sub-tabs
// ---------------------------------------------------------------------------
const TABS = ['Global Universities', 'TRL provenance', 'Listing requests', 'Emails'];

const UniversitiesPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  // Each tab mounts on its first visit and then stays mounted (hidden), so
  // flipping back and forth keeps the sheet's edits-in-progress and the TRL
  // tab's filters instead of refetching the corpus every time.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [TABS[0]]: true });
  const pick = (t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); };
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={pick} />
      {seen[TABS[0]] && <div className={tab === TABS[0] ? '' : 'hidden'}><GlobalUniversities /></div>}
      {seen[TABS[1]] && <div className={tab === TABS[1] ? '' : 'hidden'}><TrlProvenance /></div>}
      {seen[TABS[2]] && <div className={tab === TABS[2] ? '' : 'hidden'}><ListingRequests /></div>}
      {seen[TABS[3]] && (
        <div className={tab === TABS[3] ? '' : 'hidden'}>
          <EditableTemplates groups={['Universities']} keyPrefix="university_listing"
            title="University e-mails (listing requests) — edit the wording, no deploy needed" />
        </div>
      )}
    </div>
  );
};

export default UniversitiesPanel;
